import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { websiteReasoningAPI } from '../api/websiteReasoningAPI';
import { useProductBackHandler } from '../../../components/ProductBackButton/ProductBackProvider';
import { useScrollDocumentToTop } from '../../../lib/scrollDocumentToTop';
import { sanitizeHtml } from '../../../lib/sanitizeHtml';
import './WebsiteReasoningPage.css';

// Chart.js is required for numerical reasoning questions
// Install with: npm install chart.js
// Using dynamic import to handle optional dependency gracefully
let Chart = null;

const VALID_REASONING_TYPES = new Set(['logical', 'pattern', 'numerical', 'verbal']);

const WebsiteReasoningPage = ({ assessmentTypes, caseStudyContext, appContext, onComplete }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Allow /website-reasoning-test?type=logical (used by /all-apps) to preselect a suite
  const typeFromUrl = useMemo(() => {
    const raw = (searchParams.get('type') || '').trim().toLowerCase();
    return VALID_REASONING_TYPES.has(raw) ? raw : null;
  }, [searchParams]);

  const effectiveAssessmentTypes = useMemo(() => {
    if (assessmentTypes && assessmentTypes.length > 0) return assessmentTypes;
    if (typeFromUrl) return [typeFromUrl];
    return null;
  }, [assessmentTypes, typeFromUrl]);

  const skipSelection = Boolean(effectiveAssessmentTypes && effectiveAssessmentTypes.length > 0);
  const initialType = skipSelection ? effectiveAssessmentTypes[0] : null;

  // State management — when ?type= is present, skip the suite picker and go straight into the quiz
  const [view, setView] = useState(skipSelection ? 'quiz' : 'selection');
  const [selectedType, setSelectedType] = useState(initialType);
  const [selectedTime, setSelectedTime] = useState(300);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Quiz state
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [score, setScore] = useState(0);
  const [questionNumber, setQuestionNumber] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [remainingTime, setRemainingTime] = useState(
    effectiveAssessmentTypes && effectiveAssessmentTypes.length > 0 ? 300 : 0
  );
  const [feedback, setFeedback] = useState(null);
  const [quizComplete, setQuizComplete] = useState(false);
  const [results, setResults] = useState(null);
  const [showNext, setShowNext] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Refs
  const timerIntervalRef = useRef(null);
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const quizIdRef = useRef(null);
  const autoStartAttemptedRef = useRef(false);
  const startInFlightRef = useRef(false);
  const nextInFlightRef = useRef(false);
  const submitInFlightRef = useRef(false);
  const answeredForQuestionRef = useRef(null);

  // Suite picker, quiz, and results replace each other on this route.
  useScrollDocumentToTop(view);

  const timeOptions = [
    { label: '3 min', value: 180 },
    { label: '5 min', value: 300 },
    { label: '10 min', value: 600 },
    { label: '15 min', value: 900 },
    { label: '30 min', value: 1800 }
  ];

  const quizTypes = [
    {
      type: 'logical',
      title: 'Logical Reasoning',
      subtitle: 'Pattern matrices & spatial logic',
      description: 'Dynamic pattern matrices with rotation, reflection, color logic, shape progression, and spatial transformations. Every question is unique.'
    },
    {
      type: 'pattern',
      title: 'Pattern Recognition',
      subtitle: 'Number & letter sequences',
      description: 'Arithmetic, geometric, fibonacci, primes, alphabet skips, powers, and custom progressions. AI-generated with no repetition.'
    },
    {
      type: 'numerical',
      title: 'Numerical Reasoning',
      subtitle: 'Live data visualization & analysis',
      description: 'Dynamic charts, tables, percentage calculations, and trend analysis with randomized realistic business data.'
    },
    {
      type: 'verbal',
      title: 'Verbal Reasoning',
      subtitle: 'Business comprehension & inference',
      description: 'Complex business passages testing inference, causation, fact extraction, and logical reasoning with mixed relevant data.'
    }
  ];

  const getTypeTitle = (type) => {
    const typeMap = {
      'logical': 'Logical Reasoning',
      'pattern': 'Pattern Recognition',
      'numerical': 'Numerical Reasoning',
      'verbal': 'Verbal Reasoning'
    };
    return typeMap[type] || type;
  };


  const handleStartQuiz = async () => {
    if (!selectedType) return;
    if (startInFlightRef.current) return;
    startInFlightRef.current = true;

    setLoading(true);
    setError(null);
    setRemainingTime(selectedTime);

    try {
      // If assessment types provided (from app experience or ?type=), use those instead of selectedType
      const quizType = effectiveAssessmentTypes && effectiveAssessmentTypes.length > 0 
        ? effectiveAssessmentTypes[0]  // Use first assessment type from app
        : selectedType;
      
      const requestData = {
        type: quizType,
        time_limit: selectedTime
      };
      
      // Add case study context if available (for personalization)
      if (caseStudyContext) {
        requestData.case_study_context = caseStudyContext;
      }
      
      // Add assessment types if provided
      if (effectiveAssessmentTypes && effectiveAssessmentTypes.length > 0) {
        requestData.assessment_types = effectiveAssessmentTypes;
      }
      
      const response = await websiteReasoningAPI.startQuiz(quizType, selectedTime, requestData);
      
      if (response.status === 'success' && response.quiz_id) {
        quizIdRef.current = response.quiz_id;
        setTotalQuestions(response.total_questions);
        setView('quiz');
        await loadQuestion(response.quiz_id);
      } else if (response.status === 'success') {
        // Older backends without quiz_id cannot retain session across API hosts
        throw new Error('Quiz started but no quiz_id returned. Please refresh and try again.');
      } else {
        throw new Error(response.message || 'Failed to start quiz');
      }
    } catch (err) {
      setError(err.message || 'Failed to start quiz');
      setLoading(false);
    } finally {
      startInFlightRef.current = false;
    }
  };

  const loadQuestion = async (quizIdOverride) => {
    const quizId = quizIdOverride || quizIdRef.current;
    if (!quizId) {
      setError('No active quiz');
      setLoading(false);
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      const question = await websiteReasoningAPI.getQuestion(quizId);
      answeredForQuestionRef.current = null;
      setCurrentQuestion(question);
      setQuestionNumber(question.question_number);
      setSelectedAnswer(null);
      setFeedback(null);
      setShowNext(false);
      const timeLeft = Number(question.remaining_time);
      setRemainingTime(Number.isFinite(timeLeft) ? timeLeft : selectedTime);
      startTimer(Number.isFinite(timeLeft) ? timeLeft : selectedTime);
      return true;
    } catch (err) {
      if (err.message.includes('complete') || err.message.includes('expired')) {
        endQuiz();
      } else {
        setError(err.message || 'Failed to load question');
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  const startTimer = (initialTime) => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }

    let timeLeft = initialTime;
    timerIntervalRef.current = setInterval(() => {
      timeLeft--;
      setRemainingTime(timeLeft);

      if (timeLeft <= 0) {
        clearInterval(timerIntervalRef.current);
        alert('Time expired!');
        endQuiz();
      }
    }, 1000);
  };

  const renderChart = async (chartData) => {
    // Dynamically import Chart.js if not already loaded
    if (!Chart) {
      try {
        const chartModule = await import('chart.js/auto');
        Chart = chartModule.default || chartModule;
      } catch (e) {
        console.warn('Chart.js is not available. Please install it: npm install chart.js');
        return;
      }
    }

    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
      chartInstanceRef.current = null;
    }

    if (!chartRef.current || !chartData) return;

    const ctx = chartRef.current.getContext('2d');
    const t = chartData.type;
    const isPieLike = t === 'pie' || t === 'doughnut';

    const plugins = {
      title: {
        display: true,
        text: chartData.title,
        font: { size: 15, weight: '600' },
        padding: { top: 4, bottom: 12 }
      },
      legend: {
        display: true,
        position: isPieLike ? 'right' : 'top',
        labels: {
          padding: 12,
          usePointStyle: true,
          boxWidth: 10
        }
      }
    };

    if (chartData.show_amounts && isPieLike) {
      plugins.legend.labels.generateLabels = (chart) => {
        const data = chart.data;
        const ds = data.datasets[0];
        return data.labels.map((label, i) => ({
          text: `${label}: ${ds.data[i]}${t === 'pie' ? '%' : ''}`,
          fillStyle: Array.isArray(ds.backgroundColor) ? ds.backgroundColor[i] : ds.backgroundColor,
          strokeStyle: '#fff',
          lineWidth: 1,
          pointStyle: 'rectRounded',
          index: i
        }));
      };
    }

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins
    };

    if (t === 'doughnut' && chartData.cutout) {
      options.cutout = chartData.cutout;
    }
    if (chartData.indexAxis === 'y') {
      options.indexAxis = 'y';
    }

    if (t === 'line') {
      options.scales = {
        y: { beginAtZero: false, grid: { color: 'rgba(0,0,0,0.07)' } },
        x: { grid: { display: false } }
      };
    } else if (t === 'bar') {
      const stacked = !!chartData.stacked;
      const horiz = chartData.indexAxis === 'y';
      options.scales = {
        x: {
          beginAtZero: true,
          stacked,
          title: { display: horiz, text: horiz ? (chartData.datasets[0]?.label || 'Scale') : '' }
        },
        y: {
          beginAtZero: true,
          stacked,
          title: { display: !horiz, text: !horiz ? (chartData.datasets[0]?.label || 'Scale') : '' }
        }
      };
    } else if (t === 'polarArea') {
      options.scales = { r: { beginAtZero: true } };
    } else if (t === 'radar') {
      options.scales = {
        r: {
          beginAtZero: true,
          ticks: { showLabelBackdrop: false }
        }
      };
    } else if (t === 'bubble') {
      options.scales = {
        x: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.06)' } },
        y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.06)' } }
      };
    } else if (isPieLike) {
      options.scales = {};
    }

    if (t === 'bar' && chartData.histogram_style) {
      options.datasets = {
        bar: {
          categoryPercentage: 1.0,
          barPercentage: 0.9,
          borderRadius: 2
        }
      };
    }

    const config = {
      type: t,
      data: {
        labels: chartData.labels,
        datasets: (chartData.datasets || []).map((ds) => ({
          ...ds,
          borderWidth: t === 'line' ? 2 : 1,
          pointRadius: t === 'line' ? 3 : undefined,
          pointHoverRadius: t === 'line' ? 5 : undefined,
          hoverOffset: isPieLike ? 10 : 0
        }))
      },
      options
    };

    chartInstanceRef.current = new Chart(ctx, config);
  };

  // Mount chart after React paints the canvas (fixes blank numerical charts)
  useEffect(() => {
    let cancelled = false;
    const paint = async () => {
      if (!currentQuestion?.chart_data) {
        if (chartInstanceRef.current) {
          chartInstanceRef.current.destroy();
          chartInstanceRef.current = null;
        }
        return;
      }
      await new Promise((resolve) => requestAnimationFrame(() => resolve()));
      if (cancelled) return;
      await renderChart(currentQuestion.chart_data);
    };
    paint();
    return () => {
      cancelled = true;
    };
  }, [currentQuestion]);

  const handleSubmitAnswer = async () => {
    if (!selectedAnswer || feedback || submitting || submitInFlightRef.current) {
      return;
    }

    // Prevent re-submit on the same served question (button swap / double click)
    const qKey = currentQuestion?.question_number ?? questionNumber;
    if (answeredForQuestionRef.current === qKey) {
      return;
    }

    submitInFlightRef.current = true;
    answeredForQuestionRef.current = qKey;
    setSubmitting(true);
    setError(null);

    try {
      const response = await websiteReasoningAPI.submitAnswer(selectedAnswer, quizIdRef.current);

      if (response) {
        setScore(response.score ?? score);
        setFeedback(response);
        setShowNext(true);

        if (response.quiz_complete) {
          setQuizComplete(true);
          setTimeout(() => {
            endQuiz();
          }, 2500);
        }
      } else {
        // Unblock so the user can retry if the API returned an empty body
        answeredForQuestionRef.current = null;
      }
    } catch (err) {
      console.error('Error submitting answer:', err);
      answeredForQuestionRef.current = null;
      setError(err.message || 'Failed to submit answer');
    } finally {
      submitInFlightRef.current = false;
      setSubmitting(false);
    }
  };

  const resetForAnotherTest = () => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
      chartInstanceRef.current = null;
    }

    quizIdRef.current = null;
    autoStartAttemptedRef.current = false;
    startInFlightRef.current = false;
    nextInFlightRef.current = false;
    submitInFlightRef.current = false;
    answeredForQuestionRef.current = null;

    setScore(0);
    setQuestionNumber(0);
    setTotalQuestions(0);
    setResults(null);
    setCurrentQuestion(null);
    setSelectedAnswer(null);
    setFeedback(null);
    setShowNext(false);
    setQuizComplete(false);
    setError(null);
    setLoading(false);
    setSubmitting(false);
    setSelectedTime(300);
    setRemainingTime(300);

    if (skipSelection) {
      // Opened via ?type= or app embed — restart the same suite (selection is skipped)
      const type = typeFromUrl || effectiveAssessmentTypes?.[0] || selectedType;
      setSelectedType(type);
      setView('quiz');
    } else {
      setSelectedType(null);
      setView('selection');
    }
  };

  const handleNextQuestion = async () => {
    if (quizComplete) {
      endQuiz();
      return;
    }
    if (nextInFlightRef.current || loading) return;
    nextInFlightRef.current = true;
    try {
      // Keep feedback / Next visible until the next question actually loads
      const ok = await loadQuestion();
      if (!ok) {
        // Restore Next so user can retry without re-answering
        setShowNext(true);
      }
    } finally {
      nextInFlightRef.current = false;
    }
  };

  const endQuiz = async () => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }
    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
    }

    try {
      const results = await websiteReasoningAPI.endQuiz(quizIdRef.current);
      setResults(results);
      setView('results');
    } catch (err) {
      // Use current state as fallback
      setResults({
        score: score,
        total_questions: totalQuestions,
        elapsed_time: 0,
        percentage: Math.round((score / totalQuestions) * 100),
        time_per_question: 0
      });
      setView('results');
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Cleanup effect
  useEffect(() => {
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }
    };
  }, []);

  // Keep state in sync if ?type= arrives after mount (or changes via back/forward)
  useEffect(() => {
    if (!typeFromUrl) return;
    setSelectedType(typeFromUrl);
    setView('quiz');
    setSelectedTime(300);
    setRemainingTime(300);
    setCurrentQuestion(null);
    setSelectedAnswer(null);
    setFeedback(null);
    setShowNext(false);
    setError(null);
    quizIdRef.current = null;
    autoStartAttemptedRef.current = false;
  }, [typeFromUrl]);

  // Never show the suite picker while ?type= is in the URL (incl. history navigation)
  useEffect(() => {
    if (typeFromUrl && view === 'selection') {
      setView('quiz');
    }
  }, [typeFromUrl, view]);

  // Auto-start quiz once when embedded or opened via ?type= (do not retry forever on API errors)
  useEffect(() => {
    if (
      effectiveAssessmentTypes &&
      effectiveAssessmentTypes.length > 0 &&
      view === 'quiz' &&
      selectedType &&
      !currentQuestion &&
      !loading &&
      !autoStartAttemptedRef.current
    ) {
      autoStartAttemptedRef.current = true;
      const timer = setTimeout(() => {
        handleStartQuiz();
      }, 250);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveAssessmentTypes, view, selectedType, currentQuestion, loading]);

  const handleProductBack = useCallback(() => {
    if (caseStudyContext) return;
    // Typed launches (?type=) never return to the suite picker — go back to Apps
    if (view === 'quiz' && !skipSelection) {
      setSelectedType(null);
      setCurrentQuestion(null);
      setSelectedAnswer(null);
      setFeedback(null);
      setView('selection');
      return;
    }
    navigate('/apps');
  }, [caseStudyContext, view, skipSelection, navigate]);

  useProductBackHandler(caseStudyContext ? null : handleProductBack);

  // Selection View — never show when opened with ?type= (go straight into the quiz)
  if (view === 'selection' && !skipSelection) {
    return (
      <div className="website-reasoning-page">
        <main className="reasoning-main" style={{ paddingTop: 72 }}>
          <div className="reasoning-container">
            <div className="reasoning-header-section">
              <h1>Reasoning Test Suite</h1>
              <p>Dynamic question generation with intelligent difficulty adaptation</p>
            </div>

            <div className="quiz-grid">
              {quizTypes.map((quizType) => (
                <div
                  key={quizType.type}
                  className={`quiz-option ${selectedType === quizType.type ? 'selected' : ''}`}
                  onClick={() => setSelectedType(quizType.type)}
                  tabIndex="0"
                  aria-label={`${quizType.title} Test`}
                >
                  <div className="quiz-option-title">{quizType.title}</div>
                  <div className="quiz-option-subtitle">{quizType.subtitle}</div>
                  <div className="quiz-option-description">{quizType.description}</div>
                </div>
              ))}
            </div>

            <div className="time-selector">
              <h3>Choose Test Duration</h3>
              <div className="time-options">
                {timeOptions.map((option) => (
                  <div
                    key={option.value}
                    className={`time-option ${selectedTime === option.value ? 'selected' : ''}`}
                    onClick={() => setSelectedTime(option.value)}
                    aria-label={`${option.label} test`}
                  >
                    {option.label}
                  </div>
                ))}
              </div>
              <button
                className="start-button"
                onClick={handleStartQuiz}
                disabled={!selectedType || loading}
              >
                {loading ? 'Starting Test...' : selectedType 
                  ? `Start ${getTypeTitle(selectedType)} Test (${Math.floor(selectedTime / 60)}min)`
                  : 'Select a test type to begin'}
              </button>
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <div className="footer-note">
              All tests feature dynamic question generation and comprehensive performance analytics
            </div>
          </div>
        </main>

        {loading && (
          <div className="loading-overlay">
            <div className="loading-content">
              <div className="spinner"></div>
              <h3>Initializing Test Engine...</h3>
              <p>Generating unique question patterns</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Quiz View
  if (view === 'quiz') {
    return (
      <div className="website-reasoning-page quiz-view">
        <div className="quiz-container">
          <div className="quiz-header">
            <div className="quiz-info">
              <h2>{currentQuestion ? getTypeTitle(currentQuestion.type) : 'Reasoning Test'}</h2>
              <div className="quiz-progress">
                Question {questionNumber} | Score: {score}
              </div>
            </div>
            <div className="timer">
              <div className={`timer-display ${remainingTime < 30 ? 'critical' : remainingTime < 60 ? 'warning' : ''}`}>
                {formatTime(remainingTime)}
              </div>
            </div>
          </div>

          {(!currentQuestion) && (
            <div className="loading">
              <div className="loading-spinner"></div>
              <h3>{loading ? 'Generating Question...' : 'Starting test…'}</h3>
            </div>
          )}

          {currentQuestion && (
            <div className="question-container">
              {(() => {
                const showPassageLayout = Boolean(currentQuestion.passage) || currentQuestion.type === 'verbal';
                const passageText = currentQuestion.passage
                  || (currentQuestion.type === 'verbal'
                    ? 'Read the guidance carefully, then answer the question on the right.'
                    : '');
                return showPassageLayout ? (
                <div className="reasoning-split-layout">
                  {/* Left Column: Passage */}
                  <div className="reasoning-passage-pane">
                    <div className="passage-label">Passage</div>
                    <div className="passage">{passageText}</div>
                  </div>
                  
                  {/* Right Column: Question Content */}
                  <div className="reasoning-question-pane">
                    <div className="question-title">{currentQuestion.question}</div>
                    
                    {currentQuestion.grid && (
                      <div className="grid-display">
                        {currentQuestion.grid.map((row, rIdx) => (
                          <React.Fragment key={rIdx}>
                            {row.map((cell, cIdx) => (
                              <div
                                key={`${rIdx}-${cIdx}`}
                                className={`grid-cell ${cell === '?' ? 'missing' : ''}`}
                              >
                                {cell}
                              </div>
                            ))}
                          </React.Fragment>
                        ))}
                      </div>
                    )}

                    {currentQuestion.sequence && (
                      <div className="sequence-display">
                        {currentQuestion.sequence.map((item, idx) => (
                          <React.Fragment key={idx}>
                            {idx > 0 && <div className="sequence-arrow">→</div>}
                            <div className={`sequence-item ${item.display === '?' ? 'missing' : ''}`}>
                              {item.display}
                            </div>
                          </React.Fragment>
                        ))}
                      </div>
                    )}

                    {currentQuestion.odd_cells && (
                      <div className="odd-one-out-display">
                        <div className="odd-head">Positions run A–D from left to right.</div>
                        <div className="odd-cells">
                          {currentQuestion.odd_cells.map((cell, idx) => (
                            <div key={idx} className="odd-cell">
                              <span className="odd-pos">POS {String.fromCharCode(65 + idx)}</span>
                              <span className="odd-val">{cell}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {currentQuestion.chart_data && (
                      <div className="chart-container">
                        {currentQuestion.chart_data.legend_note && (
                          <div className="chart-legend-note">{currentQuestion.chart_data.legend_note}</div>
                        )}
                        <div className="chart-wrapper">
                          <canvas ref={chartRef}></canvas>
                        </div>
                      </div>
                    )}

                    {currentQuestion.table_html && (
                      <div 
                        className="data-table-container"
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(currentQuestion.table_html) }}
                      />
                    )}

                    <div className="options-container">
                      {currentQuestion.options.map((option) => (
                        <div
                          key={option.label}
                          className={`option ${selectedAnswer === option.label ? 'selected' : ''} ${feedback && feedback.correct_answer === option.label ? 'correct-answer' : ''}`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!feedback && !submitting) {
                              setSelectedAnswer(option.label);
                            }
                          }}
                          style={{
                            pointerEvents: feedback || submitting ? 'none' : 'auto',
                            cursor: feedback || submitting ? 'not-allowed' : 'pointer',
                            opacity: feedback && feedback.correct_answer !== option.label ? 0.7 : 1
                          }}
                        >
                          <span className="option-label">{option.label}</span>
                          <span>{option.text}</span>
                        </div>
                      ))}
                    </div>

                    {feedback && (
                      <div className={`feedback ${feedback.correct ? 'correct' : 'incorrect'}`}>
                        <div className="feedback-header">
                          <span>{feedback.correct ? '✅' : '❌'}</span>
                          <strong>{feedback.correct ? 'Correct!' : 'Incorrect'}</strong>
                        </div>
                        <div className="feedback-content">
                          {!feedback.correct && (
                            <div><strong>Correct Answer:</strong> {feedback.correct_answer}</div>
                          )}
                          <div>{feedback.explanation}</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  <div className="question-title">{currentQuestion.question}</div>
                  
                  {currentQuestion.grid && (
                    <div className="grid-display">
                      {currentQuestion.grid.map((row, rIdx) => (
                        <React.Fragment key={rIdx}>
                          {row.map((cell, cIdx) => (
                            <div
                              key={`${rIdx}-${cIdx}`}
                              className={`grid-cell ${cell === '?' ? 'missing' : ''}`}
                            >
                              {cell}
                            </div>
                          ))}
                        </React.Fragment>
                      ))}
                    </div>
                  )}

                  {currentQuestion.sequence && (
                    <div className="sequence-display">
                      {currentQuestion.sequence.map((item, idx) => (
                        <React.Fragment key={idx}>
                          {idx > 0 && <div className="sequence-arrow">→</div>}
                          <div className={`sequence-item ${item.display === '?' ? 'missing' : ''}`}>
                            {item.display}
                          </div>
                        </React.Fragment>
                      ))}
                    </div>
                  )}

                  {currentQuestion.odd_cells && (
                    <div className="odd-one-out-display">
                      <div className="odd-head">Positions run A–D from left to right.</div>
                      <div className="odd-cells">
                        {currentQuestion.odd_cells.map((cell, idx) => (
                          <div key={idx} className="odd-cell">
                            <span className="odd-pos">POS {String.fromCharCode(65 + idx)}</span>
                            <span className="odd-val">{cell}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {currentQuestion.chart_data && (
                    <div className="chart-container">
                      {currentQuestion.chart_data.legend_note && (
                        <div className="chart-legend-note">{currentQuestion.chart_data.legend_note}</div>
                      )}
                      <div className="chart-wrapper">
                        <canvas ref={chartRef}></canvas>
                      </div>
                    </div>
                  )}

                  {currentQuestion.table_html && (
                    <div 
                      className="data-table-container"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(currentQuestion.table_html) }}
                    />
                  )}

                  <div className="options-container">
                    {currentQuestion.options.map((option) => (
                      <div
                        key={option.label}
                        className={`option ${selectedAnswer === option.label ? 'selected' : ''} ${feedback && feedback.correct_answer === option.label ? 'correct-answer' : ''}`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (!feedback && !submitting) {
                            setSelectedAnswer(option.label);
                          }
                        }}
                        style={{
                          pointerEvents: feedback || submitting ? 'none' : 'auto',
                          cursor: feedback || submitting ? 'not-allowed' : 'pointer',
                          opacity: feedback && feedback.correct_answer !== option.label ? 0.7 : 1
                        }}
                      >
                        <span className="option-label">{option.label}</span>
                        <span>{option.text}</span>
                      </div>
                    ))}
                  </div>

                  {feedback && (
                    <div className={`feedback ${feedback.correct ? 'correct' : 'incorrect'}`}>
                      <div className="feedback-header">
                        <span>{feedback.correct ? '✅' : '❌'}</span>
                        <strong>{feedback.correct ? 'Correct!' : 'Incorrect'}</strong>
                      </div>
                      <div className="feedback-content">
                        {!feedback.correct && (
                          <div><strong>Correct Answer:</strong> {feedback.correct_answer}</div>
                        )}
                        <div>{feedback.explanation}</div>
                      </div>
                    </div>
                  )}
                </>
              );
              })()}
            </div>
          )}

          <div className="controls">
            <button type="button" className="btn btn-secondary" onClick={endQuiz}>
              End Test
            </button>
            <div style={{ display: 'flex', gap: '12px' }}>
              {!showNext ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSubmitAnswer}
                  disabled={!selectedAnswer || submitting || !!feedback}
                >
                  {submitting ? 'Submitting…' : 'Submit Answer'}
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleNextQuestion}
                  disabled={loading}
                >
                  {loading ? 'Loading…' : (quizComplete ? 'Finishing...' : 'Next Question')}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Results View
  if (view === 'results' && results) {
    const percentage = Number(results.percentage);
    const timePerQ = Number(results.time_per_question);
    return (
      <div className="website-reasoning-page results-view">
        <div className="results-container">
          <div className="results-score">
            {Number.isFinite(percentage) ? percentage.toFixed(1) : '0.0'}%
          </div>
          <h2 className="results-title">Test Complete!</h2>
          <div className="results-details">
            <div className="results-stat">
              <div className="stat-value">{results.score ?? 0}</div>
              <div className="stat-label">Correct Answers</div>
            </div>
            <div className="results-stat">
              <div className="stat-value">{results.total_questions ?? 0}</div>
              <div className="stat-label">Total Questions</div>
            </div>
            <div className="results-stat">
              <div className="stat-value">
                {formatTime(results.elapsed_time || 0)}
              </div>
              <div className="stat-label">Total Time</div>
            </div>
            <div className="results-stat">
              <div className="stat-value">
                {Number.isFinite(timePerQ) ? timePerQ.toFixed(1) : '0.0'}s
              </div>
              <div className="stat-label">Per Question</div>
            </div>
          </div>
          {onComplete ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={onComplete}
            >
              Continue to Next Step
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-primary"
              onClick={resetForAnotherTest}
            >
              Take Another Test
            </button>
          )}
        </div>
      </div>
    );
  }

  return null;
};

export default WebsiteReasoningPage;

